/**
 * Grille de réponses réutilisable (aperçu impression).
 *
 * Une liste d'items courts (« a. AB ? CD ……… ») gaspille la feuille en
 * colonne unique. La classe CSS `.ep-grille-reponses` (eval-print.css) met
 * les items sur autant de colonnes que la PLACE RÉELLE le permet — zoom du
 * bloc compris — avec un plafond de 4 colonnes en pleine largeur et 2 sous
 * `.ep-col` (demi-colonne de masonry). Le renderer ne fournit qu'une chose :
 * la largeur NÉCESSAIRE d'un item, via la variable `--ep-besoin`
 * (`styleGrille`). Le minimum de piste est borné par `min(besoin, 100 %)` :
 * la grille ne déborde jamais de son conteneur, donc le scan de refus des
 * demi-colonnes du moteur (scrollWidth > clientWidth) n'est jamais déclenché
 * à tort.
 *
 * Utilisé par : listes de tableaux en réponse libre et en mode cahier
 * (PrintVraiFaux / PrintVraiFauxGroup), corrigé de ces tableaux
 * (CorrectionsSection). Tout composant « liste de réponses courtes » peut
 * s'en servir : `className="… ep-grille-reponses" style={styleGrille(besoin)}`.
 */

// Géométrie Confort (px, border-box), pour l'ESTIMATEUR seulement (la grille
// CSS mesure la vraie place) : feuille 21cm = 793,7 − 2 × 52,9 de padding =
// 687,9 ; carte .ep-exo −43 ; liste −22 → 623 px en pleine largeur,
// (687,9 − 16) / 2 − 65 = 271 px en demi-colonne ; −28 px dans un groupe.
export const DISPO_FULL = 623;
export const DISPO_HALF = 271;
export const GROUPE_PAD = 28;
export const GRID_GAP = 12;

const REPERE_W = 16;      // « a. »
const GAP_W = 8;          // entre texte et ligne
const SECURITE = 6;
const PX_PAR_CAR = 7;     // 0,85 rem = 13,6 px, glyphe moyen ≈ 0,5 em

/** Longueur affichée approximative d'un fragment HTML (balises retirées, une
 *  commande KaTeX = un caractère). */
export const longueurTexte = (html) => String(html ?? '')
    .replace(/<[^>]*>/g, '')
    .replace(/\\[a-zA-Z]+/g, 'x')
    .replace(/\$/g, '')
    .trim()
    .length;

/** Largeur de la ligne de réponse d'un tableau : Vrai/Faux tient en 90 px ;
 *  un libellé de colonne plus long (« Non constructible ») élargit la ligne
 *  (≈ 8 px par caractère), un symbole (< = >) la réduit à 50 px. */
export const largeurReponse = (colonnes, vraiFaux) => {
    if (vraiFaux) return 90;
    const maxLen = (colonnes || []).reduce((m, c) => Math.max(m, String(c ?? '').length), 0);
    return Math.min(260, Math.max(50, maxLen * 8 + 16));
};

/** Largeur nécessaire (px) d'un item « repère + texte + ligne éventuelle ». */
export const besoinItemPx = ({ texteMax, repW = 0, repere = REPERE_W }) =>
    repere + PX_PAR_CAR * texteMax + (repW ? GAP_W + repW : 0) + SECURITE;

/** Style à poser sur le conteneur `.ep-grille-reponses`. */
export const styleGrille = (besoin) => ({ '--ep-besoin': `${Math.round(besoin)}px` });

/** Nombre de colonnes que la grille CSS choisira, approché pour l'estimateur
 *  de hauteur (la mesure DOM fait foi). `dispo` en px nominaux, `zoom` du
 *  bloc (px locaux = dispo / zoom), `cap` = plafond (4 pleine, 2 demi). */
export const colonnesEstimees = ({ besoin, dispo, zoom = 1, cap = 4 }) => {
    const local = dispo / Math.max(zoom, 0.05);
    return Math.max(1, Math.min(cap, Math.floor((local + GRID_GAP) / (besoin + GRID_GAP))));
};
